import fb from '@/firebase'
import env from '@/env'
import { TeamState, RootState, UnsubscribeFunction, FirestoreQuerySnapper, FirestoreSnapperType, FirestoreDocSnapper } from '@/store/types'
import { Team, MemberInvite, TeamMember, FaceBookPage, TeamGroup } from '@/types/teams'
import { Stream, DefaultStream, StreamType, SrtInfo } from '@/types/streams'
import { WarningMessage } from '@/classes/Admin/WarningMessage'
import { ActionContext } from 'vuex'
import { UserNotification, UserProfile, WebRTCEncoderSettings } from '@/types/users'
import { Layout } from '@/types/layouts'
import { some } from 'lodash'
import store, { storehelper } from '../..'
import { removeExpiredNotifications, addDefaultLayoutsToTeam, addDefaultAssetsToTeam, addDefaultCastTemplatesToTeam, DefaultIdsMapping } from './team.helpers'
import { teamModuleActionContext } from '.'
import { CollectionNames, KeyDict } from '@/types'
import { normalizeTransition } from '@/helpers/normalize'
import { firestore } from 'firebase'
import helpers from '@/helpers'
import { Region } from '@/types/ops'
import { MVar } from '@/helpers/async_helpers'


const SUMMARIES = 'billing_summaries'
//const DETAILS = 'billing_details' // future use

type TeamContext = ActionContext<TeamState, RootState>

interface TeamsSubscriptions {
  teamStreams: null | UnsubscribeFunction
  activeTeam: null | UnsubscribeFunction,
  warningMessages: null | UnsubscribeFunction
}

const subscriptions = {
  teamStreams: null,
  activeTeam: null,
  warningMessages: null
} as TeamsSubscriptions

const teamUnsubscribe = {}
const teamlayoutUnsubscribe = {}
const teamGroupsUnsubscribe = {}
const profileUnsubscribe = {}

const makeSrtInfo = (form_info : any) : SrtInfo => {
  const srtInfo : SrtInfo = {
     nimbleserver : form_info.nimbleserver,
     port : parseInt(form_info.port)
  }
  if (form_info?.ip !== undefined) {
    srtInfo.ip = form_info.ip
  }
  if (form_info?.mode !== undefined) {
    srtInfo.mode = form_info.mode
  }
  return srtInfo
}

const getPrefabStreams = (payload: { amount: number, team: Partial<Team> }): Stream[] => {
  const streams: Stream[] = []
  const teamId = payload.team.id
  const defaultRegion = payload.team.default_region
  if (defaultRegion === undefined) {
    console.error(`Unable to create prefab streams for this team (${ teamId }), no default_region found.`)
    return streams
  }

  const wrtcUrl = getWrtcUrlForRegion(defaultRegion)
  for (let i = 0; i < payload.amount; i++) {
    const name = `Input stream #${ i + 1 }`
    const srcUrl = `rtmp://${ wrtcUrl }/live/${ teamId }_${ helpers.generateRandomString(8) }`
    const stream: Stream = { name: name, srcurl: srcUrl, type: StreamType.Broadcast, templateId: `defaultinput_${ i + 1 }` }
    streams.push(stream)
  }

  return streams
}

const getWrtcUrlForRegion = (region: string) => {
  const transcodingRegions = store.state.admin.transcodingregions
  const wrtcServer = transcodingRegions[region].wrtcserver
  const wrtcKey = Object.keys(wrtcServer)[0]
  const wrtcUrl = wrtcServer[wrtcKey].url
  return wrtcUrl
}

const generateStreamId = (team: Partial<Team>): string => {
  const hash = Date.now().toString() + helpers.generateRandomString(8)
  return `${ team.id }${ hash }`
}

// actions
const actions = {
  async subscribeTeamInvites (context: TeamContext, teamId: string) {
    const { commit } = teamModuleActionContext(context)
    store.commit.assets.assetsLoading(true)
      const snapper: FirestoreQuerySnapper = {
      query: fb.db.collection('invites').where('team_id', '==', teamId),
      mutation: commit.setInvites,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe('teaminvites', snapper)
  },
  async unsubscribeTeamInvites (_context: TeamContext) {
    storehelper.unsubscribe('teaminvites')
  },
  async subscribeAllTeams (context: TeamContext) {
    const { commit } = teamModuleActionContext(context)
    const key = 'allteams'
    const snapper: FirestoreQuerySnapper = {
      query: fb.db.collection(CollectionNames.TEAMS),
      mutation: commit.setAllTeams,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe(key, snapper)
  },
  async unsubscribeAllTeams (context: TeamContext) {
    const { commit } = teamModuleActionContext(context)
    const key = 'allteams'
    storehelper.unsubscribe(key, commit.clearAllTeams)
  },
  async checkIfUserHasProfile (_context: TeamContext, email: string) {
    const res = await fb.db.collection('profiles').where('email', '==', email).get()
    return res
  },
  async inviteTeamMember (_context: TeamContext, member: MemberInvite) {
    await fb.db.collection('invites').add({ ...member })
  },
  async deleteInvite (_context: TeamContext, invite: MemberInvite) {
    await fb.db.collection('invites').doc(invite.id).delete()
  },
  async resendInvite (_context: TeamContext, invite: MemberInvite) {
    await fb.db.collection('invites').doc(invite.id).set({ email_sent: false }, { merge: true })
  },
  unsubscribeActiveTeam (_context: TeamContext) {
    if (subscriptions.activeTeam !== null) {
      subscriptions.activeTeam()
      subscriptions.activeTeam = null
    }
  },
  async setActiveTeam (context: TeamContext, teamId: string) {
    const { commit, dispatch } = teamModuleActionContext(context)
    commit.setActiveTeam(teamId)
    await dispatch.unsubscribeActiveTeam()
    // TODO: Use storehelper so we can easily waitForIt and we only need to store the subscription key instead of the
    //       unsubscribe function
    //       Applies to other `onSnapshots()` subscriptions in this module too.
    const mvar = new MVar<void>()
    subscriptions.activeTeam = fb.db.collection(CollectionNames.TEAMS).doc(teamId).onSnapshot(snap => {
      const team = snap.data() as Team|undefined   // TODO needs to go though a constructor
      if (team) {
        team.id = team.id ?? teamId // make sure id property is set
        commit.setTeam(team)
      }
      mvar.put()
    }, error => {
      console.error(`Error setActiveTeam ${teamId}`, error)
      mvar.put()
    })
    await mvar.read()
  },
  addUserToTeam (_context: TeamContext, user: UserProfile) {
    return new Promise<void>(resolve => {
      fb.db.collection(CollectionNames.TEAMS).doc(user.team_id).set({ members: { [user.id]: { role: user.role } } }, { merge: true }).then(() => {
        resolve()
      }).catch(error => {
        console.log('Error creating user: ', error)
      })
    })
  },
  async notifyTeamMember (store: TeamContext, newNotification: UserNotification ) {
    const member = store.getters.user(newNotification.user_id)
    if (!member) {
      return
    }

    removeExpiredNotifications(member)

    const now = new Date().getTime()
    const alreadyExists = some(member.notifications || {}, function (value) {
      const notExpired = value.expires > now
      return notExpired
        && value.link === newNotification.link
        &&  value.linkLabel === newNotification.linkLabel
        &&  value.user_id === newNotification.user_id
        &&  value.message === newNotification.message
    })

    if (alreadyExists) {
      console.log('Notification alreadyExists')
      return
    }

    const updateObject = {}
    updateObject[newNotification.expires] = {
      ...newNotification
    }
    if (newNotification.user_id) {
      await fb.db.collection('profiles').doc(newNotification.user_id).set({
        notifications: updateObject
      }, { merge: true })
    }
  },
  undoNotifyTeamMember: async (store: TeamContext, undoNotification: UserNotification) => {
    const member = store.getters.user(undoNotification.user_id)
    if (!member) {
      return
    }
    const deleteupdate = {}
    deleteupdate['notifications.' + undoNotification.expires] = fb.firebase.firestore.FieldValue.delete()
    await fb.db.collection('profiles').doc(member.user_id).update(deleteupdate)
  },
  removeNotification (store: TeamContext, notification: UserNotification ) {
    const member = store.getters.user(notification.user_id)
    if (!member) {
      return
    }

    removeExpiredNotifications(member)

    const deleteupdate = {}
    deleteupdate['notifications.' + notification.expires] = fb.firebase.firestore.FieldValue.delete()
    return fb.db.collection('profiles').doc(member.user_id).update(deleteupdate)
  },
  async updateTeamMember (_context: TeamContext, member: Partial<TeamMember>) {
    console.log('updateTeamMember', member)
    // TODO put the role in a different collection
    const data = { members: {} }
    if (member.user_id && member.teamid) {
      data.members[member.user_id] = { role: member.role }
      await fb.db.collection(CollectionNames.TEAMS).doc(member.teamid).set(data, { merge: true })
      delete member.teamid
      delete member.role
      await fb.db.collection('profiles').doc(member.user_id).set(member, { merge: true })
    }
  },
  setDefaultTeamLayout (_context: TeamContext, data: { team_id: string, layout: Layout }) {
    return new Promise<void>(resolve => {
      fb.db.collection(CollectionNames.TEAMS).doc(data.team_id).set({ default_layout: data.layout }, { merge: true }).then(() => {
        resolve()
      }).catch(error => {
        console.log('Error creating user: ', error)
      })
    })
  },
  async setDefaultRegion (context: TeamContext, newRegion: string) {
    if (newRegion) {
      const { state } = teamModuleActionContext(context)
      await fb.db.collection(CollectionNames.TEAMS).doc(state.team.id).set({ default_region: newRegion }, { merge: true })
    }
  },
  unsubscribeCurrentTeams (_context: TeamContext) {
    Object.keys(teamUnsubscribe).forEach(team => {
      teamUnsubscribe[team]()
      delete teamUnsubscribe[team]
    })
    Object.keys(teamlayoutUnsubscribe).forEach(team => {
      teamlayoutUnsubscribe[team]()
      delete teamlayoutUnsubscribe[team]
    })
    Object.keys(teamGroupsUnsubscribe).forEach(group => {
      teamGroupsUnsubscribe[group]()
      delete teamGroupsUnsubscribe[group]
    })
    Object.keys(profileUnsubscribe).forEach(profile => {
      profileUnsubscribe[profile]()
      delete profileUnsubscribe[profile]
    })
  },
  subscribeCurrentTeam (context: TeamContext, teamId: string) {
    const { commit } = teamModuleActionContext(context)
    commit.clearTeamStreams()
    if (teamUnsubscribe[teamId] === undefined) {
      commit.setTeamLoaded(false)
      // TODO make unsubscribe for this function
      teamlayoutUnsubscribe[teamId] = fb.db.collection('layouts').where('team_id', '==', teamId).onSnapshot(snaps => {
        const layouts: Layout[] = []
        for (let i = 0; i < snaps.docs.length; i++) {
          const id = snaps.docs[i].id
          const layout = snaps.docs[i].data() as Layout
          if (layout) {
            layout.transition = normalizeTransition(layout.transition)
            layouts.push({ id, ...layout })
          }
        }
        commit.setTeamLayouts(layouts)
      }, error => {
        console.error('error onSnapshot layouts ' + teamId + ' ' + error)
      })
      teamGroupsUnsubscribe[teamId] = fb.db.collection('team_groups').where('team_id', '==', teamId).onSnapshot(snaps => {
        const groups: TeamGroup[] = []
        for (let i = 0; i < snaps.docs.length; i++) {
          const id = snaps.docs[i].id
          const group = snaps.docs[i].data() as TeamGroup
          if (group) {
            groups.push({ id, ...group })
          }
        }
        commit.setTeamGroups(groups)
      }, error => {
        console.error('error onSnapshot team groups ' + teamId + ' ' + error)
      })
      teamUnsubscribe[teamId] = fb.db.collection(CollectionNames.TEAMS).doc(teamId).onSnapshot(snap => {
        commit.clearTeamMembers() //Might not be needed
        const snapData = snap.data()
        if (!snapData) return
        // const members = snapData.members
        commit.setTeamDefaultLayout(snapData.default_layout)
        commit.setTeamScenePresets(snapData.scene_presets)
        Object.keys(profileUnsubscribe).forEach(profile => {
          profileUnsubscribe[profile]()
          delete profileUnsubscribe[profile]
        })
        profileUnsubscribe[teamId] = fb.db.collection('profiles').where('teams.' + teamId, '==', true).onSnapshot(snapMembers => {
          const members: UserProfile[] = []
          const memberDocs = snapMembers.docs
          for (let i = 0; i < memberDocs.length; i++) {
            const tmpMember = memberDocs[i].data()
            const member = snapData.members[tmpMember.id]
            if (tmpMember && member) {
              let displayName = ''
              let displayImage = ''
              let avatar = ''
              let teamGroups = {}
              let legalAgreements = {
                tosVersion: 0,
                privacyVersion: 0,
                lastUpdateDialog: 0,
              }
              if (tmpMember.hasOwnProperty('display_name')) {
                if (tmpMember.display_name !== '') {
                  displayName = tmpMember.display_name
                }
              }
              if (tmpMember.hasOwnProperty('display_image')) {
                if (tmpMember.display_image !== '') {
                  displayImage = tmpMember.display_image
                }
              }
              if (tmpMember.hasOwnProperty('avatar')) {
                if (tmpMember.avatar !== '') {
                  avatar = tmpMember.avatar
                }
              }
              if (tmpMember.hasOwnProperty('legalAgreements')) {
                legalAgreements = tmpMember.legalAgreements
              }
              if (tmpMember.hasOwnProperty('teamGroups')) {
                teamGroups = tmpMember.teamGroups
              }
              let transcodingregion = 'none'
              if (tmpMember.hasOwnProperty('transcodingregion')) {
                transcodingregion = tmpMember.transcodingregion
              }
              let encoderSettings: WebRTCEncoderSettings = {}
              if (tmpMember.hasOwnProperty('encoderSettings')) {
                encoderSettings = tmpMember.encoderSettings as WebRTCEncoderSettings
              }

              let notifications = {}
              // uncomment to allow notifications
              if (tmpMember.hasOwnProperty('notifications')) {
                notifications = tmpMember.notifications
              }
              const tempRole = snapData.members[tmpMember.id].role !== undefined ? snapData.members[tmpMember.id].role : 'member'

              const data = {
                id: tmpMember.id,
                user_id: tmpMember.id,
                role: tempRole,
                first_name: tmpMember.first_name,
                last_name: tmpMember.last_name,
                display_name: displayName,
                avatar: avatar,
                email: tmpMember.email,
                display_image: displayImage,
                transcodingregion,
                notifications,
                selftest: undefined,
                echoSuppression: tmpMember.echoSuppression !== undefined ? tmpMember.echoSuppression : false,
                udpEnabled: tmpMember.udpEnabled !== undefined ? tmpMember.udpEnabled : false,
                encoderSettings,
                legalAgreements,
                teamGroups,
                anonymous: !!tmpMember.anonymous
              } as UserProfile

              if (tmpMember.hasOwnProperty('chromaKeyPreviewQuality')) {
                if (tmpMember.chromaKeyPreviewQuality !== '') {
                  data.chromaKeyPreviewQuality = tmpMember.chromaKeyPreviewQuality
                }
              }
              if (tmpMember.hasOwnProperty('previewVideoQuality')) {
                if (tmpMember.previewVideoQuality !== '') {
                  data.previewVideoQuality = tmpMember.previewVideoQuality
                }
              }

              const permission = true
              if (permission) { // TODO -> need to check if we have permissions for this
                data.selftest = tmpMember.selftest
              }
              //
              members.push(data)
            }
          }

          commit.loadTeamMembers(members)
          commit.setTeamLoaded(true)
        })
      }, error => {
        console.error('error subscribeCurrentTeam ' + teamId + ' ' + error)
      })
    }
  },
  async getTeamCastProperties (context: TeamContext) {
    const { commit } = teamModuleActionContext(context)
    const snapper: FirestoreQuerySnapper = {
      query: fb.db.collection('cast_properties'),
      mutation: commit.setCastProperties,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe('castProperties', snapper)
  },
  async getDefaultStreams (context: TeamContext) {
    const { commit } = teamModuleActionContext(context)
    const snap = await fb.db.collection('admin').doc('defaultstreams').get()
    if (snap.exists) {
      const data = snap.data()
      const streams: DefaultStream[] = []
      if (data !== undefined) {
        for (const stream of data.streams ?? []) {
          streams.push(new DefaultStream(stream))
        }
      }
      commit.setDefaultStreams(streams)
    }
  },
  unsubscribeWarningMessages (_context: TeamContext) {
    if (subscriptions.warningMessages !== null) {
      subscriptions.warningMessages()
    }
  },
  subscribeWarningMessages (context: TeamContext) {
    const { commit, dispatch } = teamModuleActionContext(context)
    dispatch.unsubscribeWarningMessages()
    subscriptions.warningMessages = fb.db.collection('admin').doc('updates').onSnapshot(querySnapshot => {
      const messages = querySnapshot.data() as KeyDict<WarningMessage>
      commit.setWarningMessages(messages)
    }, error => {
      console.error('subscribeWarningMessages error ', error)
    })
  },
  addWarningMessage (_context: TeamContext, message: WarningMessage) {
    const key: string = '' + (new Date()).getTime() / 1000
    fb.db.collection('admin').doc('updates').set({ [key]: message.toDict() }, { merge: true })
  },
  deleteWarningMessage (_context: TeamContext, id: string) {
    fb.db.collection('admin').doc('updates').set({ [id]: fb.firebase.firestore.FieldValue.delete() }, { merge: true })
  },
  unsubscribeTeamStreams (_context: TeamContext) {
    if (subscriptions.teamStreams !== null) {
      subscriptions.teamStreams()
      subscriptions.teamStreams = null
    }
  },
  async subscribeTeamStreams (context: TeamContext, teamId: string) {
    const { commit, dispatch } = teamModuleActionContext(context)
    await dispatch.unsubscribeTeamStreams()
    const snapper: FirestoreQuerySnapper = {
      query: fb.db.collection('streams').where('team_id', '==', teamId),
      mutation: commit.setTeamStreams,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe('teamStreams', snapper)
  },
  removeMemberFromTeam ({ dispatch }: TeamContext, member) {
    return new Promise<void>(resolve => {
      fb.db.collection(CollectionNames.TEAMS).doc(member.teamId).get().then(snap => {
        if (snap.exists) {
          const key = 'members.' + member.user_id
          fb.db.collection(CollectionNames.TEAMS).doc(member.teamId).update({ [key]: fb.firebase.firestore.FieldValue.delete() }).then(() => {
            resolve()
          }).catch(error => {
            console.error('Remove Member from Team error - update1', error)
          })
          store.dispatch.castTemplates.cleanupTeamTemplates({ id: member.user_id, itemType: 'invited_casters' })
        }
      }).catch(error => {
        console.error('Remove Member from Team error - get1', error)
      })
      fb.db.collection('profiles').doc(member.user_id).get().then(snap => {
        if (snap.exists) {
          const key = 'teams.' + member.teamId
          const updateObj = { [key]: fb.firebase.firestore.FieldValue.delete() }
          if (snap.data()?.currentTeam === member.teamId ) {
            // If another team is available set that as the new `currentTeam`, otherwise remove the property altogether.
            let newCurrentTeam: string|null = null
            for (const teamId of Object.keys(snap.data()?.teams ?? {})) {
              if (teamId !== member.teamId) {
                newCurrentTeam = teamId
                break
              }
            }
            updateObj.currentTeam = newCurrentTeam ?? fb.firebase.firestore.FieldValue.delete()
          }
          fb.db.collection('profiles').doc(member.user_id).update(updateObj).then(() => {
            resolve()
          }).catch(error => {
            console.error('Remove Member from Team error - update2', error)
          })
        }
      }).catch(error => {
        console.error('Remove Member from Team error - get2', error)
      })
      dispatch('subscribeCurrentTeam', member.teamId)
    })
  },
  async addFacebookPages (context: TeamContext, pages: KeyDict<FaceBookPage>) {
    const { state } = teamModuleActionContext(context)
    await fb.db.collection(CollectionNames.TEAMS).doc(state.activeTeam).set({
      facebook: pages
    }, {merge: true })
  },
  async removeFacebookPages (context: TeamContext) {
    const { state } = teamModuleActionContext(context)
    await fb.db.collection(CollectionNames.TEAMS).doc(state.activeTeam).update({
      facebook: fb.firebase.firestore.FieldValue.delete()
    })
  },
  async addTeamGroup (_context: TeamContext, params : { group: TeamGroup, userIds: string[] }) : Promise<void> {
    const doc_ref = await fb.db.collection('team_groups').add(params.group)
    const groupId = doc_ref.id
    await store.dispatch.user.assignTeamGroupToUsers({ userIds: params.userIds, groupId: groupId })
  },
  async editTeamGroup (_context: TeamContext, params : { group: TeamGroup, userIds: string[] }) {
    if (params.group.id) {
      await fb.db.collection('team_groups').doc(params.group.id).set(params.group)
      await store.dispatch.user.assignTeamGroupToUsers({ userIds: params.userIds, groupId: params.group.id })
    }
  },
  async removeTeamGroup (context: TeamContext, groupId: string) {
    const { state } = teamModuleActionContext(context)
    try {
      //Remove reference to group from all its members
      const batch = fb.db.batch()
      const usersSnap = await fb.db.collection('profiles').where('teamGroups.' + state.activeTeam, 'array-contains', groupId).get()
      usersSnap.docs.forEach(user => {
          batch.update(user.ref, { ['teamGroups.' + state.activeTeam]: firestore.FieldValue.arrayRemove(groupId)})
        })
      //Remove reference to group from all casts it was assigned to
      const castsSnap = await fb.db.collection('casts').where('team_id', '==', state.activeTeam).where('teamGroups', 'array-contains', groupId).get()
      castsSnap.docs.forEach(cast => {
        batch.update(cast.ref, { ['teamGroups']: firestore.FieldValue.arrayRemove(groupId)})
      })
      const groupDocRef = fb.db.collection('team_groups').doc(groupId)
      batch.delete(groupDocRef)
      batch.commit()
    } catch(error) {
      console.error(`Error removing member group ${groupId}.`, error)
      throw error; // this will end up in the 'growl' for the user. TODO: maybe reword the message?
    }
  },
  async createTeamStream (_context: TeamContext, payload: { stream: Stream, team: Partial<Team> }): Promise<Stream|undefined> {
    const { stream, team } = payload
    const streamDocId = generateStreamId(team)
    const streamDoc: Stream = {
      active: false,
      persistent: true,
      srcurl: stream.srcurl,
      name: stream.name,
      type: stream.type,
      team_id: team.id,
      team: team.id,
      deleted: false
    }
    if (stream.templateId !== undefined) {
      streamDoc.templateId = stream.templateId
    }
    if (stream.srt_info !== undefined) {
      streamDoc.srt_info = makeSrtInfo(stream.srt_info)
    }
    if (stream.type === StreamType.Output) {
      streamDoc.reencode = stream.reencode
      streamDoc.framerate = stream.framerate
      streamDoc.resolution = stream.resolution
      if (stream.rate !== undefined) {
        streamDoc.rate = stream.rate
      }
    }
    if (streamDoc.type === StreamType.Broadcast) {
      streamDoc.leftrightsplit = !!stream.leftrightsplit
      if (stream.auth_info !== undefined) {
        streamDoc.auth_info = stream.auth_info
      }
    }
    try {
      await fb.db.collection(CollectionNames.STREAMS).doc(streamDocId).set(streamDoc)
      return { ...streamDoc, id: streamDocId }
    } catch (error) {
      console.error('Error creating stream:', error)
    }
  },
  async updateTeamStream (_context: TeamContext, stream: Stream) {
    const streamdoc = { srcurl: stream.srcurl, type: stream.type, name: stream.name, deleted: false } as Stream
    if (stream.type === StreamType.Output) {
      if (stream.reencode) {
        streamdoc.reencode = stream.reencode
        streamdoc.framerate = stream.framerate
        streamdoc.resolution = stream.resolution
      } else {
        streamdoc.reencode = false
      }
      if (stream.rate !== undefined) {
        streamdoc.rate = stream.rate
      }
    }
    if (streamdoc.type === StreamType.Broadcast) {
      streamdoc.leftrightsplit = stream.leftrightsplit
      if (stream.auth_info !== undefined) {
        streamdoc.auth_info = stream.auth_info
      }
    }
    if (stream.srt_info !== undefined) {
      const srtInfo = makeSrtInfo(stream.srt_info)
      streamdoc.srt_info = srtInfo
    }
    try {
      await fb.db.collection(CollectionNames.STREAMS).doc(stream.id).set(streamdoc, { merge: true })
      const doc = (await fb.db.collection(CollectionNames.STREAMS).doc(stream.id).get()).data()
      const fieldsToDelete: KeyDict<firebase.firestore.FieldValue> = {}
      if (doc) {
        if (doc.srt_info !== undefined && streamdoc.srt_info === undefined) {
          fieldsToDelete.srt_info = fb.firebase.firestore.FieldValue.delete()
        }
        if (doc.auth_info !== undefined && streamdoc.auth_info === undefined) {
          fieldsToDelete.auth_info = fb.firebase.firestore.FieldValue.delete()
        }
      }
      if (Object.keys(fieldsToDelete).length > 0) {
        await fb.db.collection(CollectionNames.STREAMS).doc(stream.id).update(fieldsToDelete)
      }
    } catch (error) {
      console.error('Error updating stream:', error)
    }
  },
  deleteTeamStream (_context: TeamContext, stream: Stream, ) {
    console.log('deleteTeamStream', stream, stream.type)
    return new Promise<void>(resolve => {
      fb.db.collection('streams').doc(stream.id).set({ deleted: true, deleteTime: new Date() }, { merge: true }).then(() => {
        const itemType = stream.type === 'broadcast' ? 'input_streams' : 'custom_rtmp_destinations'
        if (stream.id !== undefined) {
          store.dispatch.castTemplates.cleanupTeamTemplates({ id: stream.id, itemType: itemType })
        }
        resolve()
      }).catch(error => {
        console.log('Error deleting stream: ', error)
      })
    })
  },
  async updateTeamScene (_context: TeamContext, scene: { key: string, team: string, name: string }) {
    if (scene.team && scene.key) {
      try {
        await fb.db.collection(CollectionNames.TEAMS).doc(scene.team).set({
          scene_presets: {
            [scene.key]: { name: scene.name }
          }
        }, { merge: true })
      } catch (error) {
        console.log('Error updating scene: ', error)
      }
    }
  },
  async deleteTeamScene (_context: TeamContext, scene: { key: string, team: string }) {
    const deletekey = `scene_presets.${scene.key}`
    try {
      await fb.db.collection(CollectionNames.TEAMS).doc(scene.team).update({ [deletekey]: fb.firebase.firestore.FieldValue.delete() })
    } catch (error) {
      console.log('Error deleting scene: ', error)
    }
  },
  async createTeam (context: TeamContext, team: Partial<Team>) {
    try {
      const { dispatch } = teamModuleActionContext(context)
      const payload = {
        k360_channel_id: env.k360_channel_id,
        ...JSON.parse(JSON.stringify(team)) //Ensure plain objects are sent instead of custom classes
      }
      const newTeam = await fb.db.collection(CollectionNames.TEAMS).add(payload)
      const teamId = newTeam.id

      const mappingAssets = await addDefaultAssetsToTeam(teamId)
      if (mappingAssets.assetIds === undefined || mappingAssets.assetGroupIds === undefined) {
        throw new Error('Failed to get valid default asset ids.')
      }

      await fb.db.collection(CollectionNames.TEAMS).doc(teamId).set({ id: teamId }, { merge: true })
      if (team.features?.selfServe?.enabled && team.default_region !== undefined) {
        team.streams?.push(...getPrefabStreams({ amount: 2, team: { ...team, id: teamId }}))
      }
      const streamsMapping = new Map<string, string>()
      if (team.streams) {
        const promises: Promise<Stream|undefined>[] = []
        for (const stream of team.streams) {
          promises.push(dispatch.createTeamStream({ stream, team: { ...team, id: teamId }}))
        }
        const streams = await Promise.all(promises)
        for (const stream of streams) {
          if (stream?.id === undefined ) continue
          if (stream.templateId === undefined) continue
          streamsMapping.set(stream.templateId, stream.id)
        }
      }

      const idsMapping: DefaultIdsMapping = {
        assetIds: mappingAssets.assetIds,
        assetGroupIds: mappingAssets.assetGroupIds,
        layoutIds: new Map<string, string>(),
        streamIds: streamsMapping
      }

      const mappingLayouts = await addDefaultLayoutsToTeam(teamId, idsMapping)
      if (mappingLayouts.layoutIds === undefined) {
        throw new Error('Failed to get valid default layout ids.')
      }
      idsMapping.layoutIds = mappingLayouts.layoutIds

      const defaultRegion = team.default_region as Region ?? Region.USEast
      await addDefaultCastTemplatesToTeam(teamId, idsMapping, defaultRegion)
    } catch (error) {
      console.error('Error creating new team ', error)
    }
  },
  async updateTeam (_context: TeamContext, team: Partial<Team>) {
    try {
      console.log('In Update team ', JSON.stringify(team))
      await fb.db.collection(CollectionNames.TEAMS).doc(team.id).set(team, { merge: true })
    } catch (error) {
      console.log('Error updating team: ', error)
    }
  },
  async deleteTeam (_context: TeamContext, team: { id: string }) {
    try {
      await fb.db.collection(CollectionNames.TEAMS).doc(team.id).set({ deleted: true }, { merge: true })
    } catch (error) {
      console.log('Error deleting team: ', error)
    }
  },
  activateTeam (_context: TeamContext, team: { id: string }) {
    return new Promise<void>(resolve => {
      fb.db.collection(CollectionNames.TEAMS).doc(team.id).set({ deleted: false }, { merge: true }).then(() => {
        resolve()
      }).catch(error => {
        console.log('Error deleting team: ', error)
      })
    })
  },
  resetTeam (context: TeamContext) {
    const { commit } = teamModuleActionContext(context)
    commit.resetTeam()
  },
  async recalculateBilling (context: TeamContext) {
    const { state } = teamModuleActionContext(context)
    await fb.db.collection('admin').doc('billing').set({
      [state.activeTeam]: true
    }, { merge: true })
  },
  async resyncBilling (context: TeamContext) {
    try {
      const { state } = teamModuleActionContext(context)
      const docRef = fb.db.collection('billing_summaries').doc(state.activeTeam)
      await docRef.update({ force_resync : true })
    } catch (error) {
      console.log('recyncBilling failed', error)
    }
  },
  async resyncBillingAll (_context: TeamContext) {
    try {
      const docs = await fb.db.collection('billing_summaries').get()// TODO: I only need the ids, so this is overkill
      const batch = fb.db.batch()
      for(const snap of docs.docs){
        batch.update(snap.ref, { force_resync: true })
      }
      await batch.commit()
    } catch (error) {
      console.log('resyncBillingAll failed', error)
    }
  },
  async subscribeBilling (context: TeamContext, teamId: undefined|string) {
    const { state, commit } = teamModuleActionContext(context)
    const billingTeamId = teamId ?? state.activeTeam
    const snapper: FirestoreDocSnapper = {
      doc: fb.db.collection(SUMMARIES).doc(billingTeamId),
      mutation: commit.updateBilling,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe('billing', snapper)
  },
  async unsubscribeBilling (context: TeamContext) {
    const { commit } = teamModuleActionContext(context)
    storehelper.unsubscribe('billing', commit.removeBilling)
  }
}

export default actions
